import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { PhoneCall, PhoneMissed, TrendingUp, Timer, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchNumberCalls, fetchNumberStats } from "@/lib/api";
import { useNumbers } from "@/context/NumberContext";
import { StatCard } from "@/components/StatCard";
import { CallList } from "@/components/CallList";
import { CallCards } from "@/components/CallCards";
import { CallDetailPanel } from "@/components/CallDetailPanel";
import { CallDetailOverlay } from "@/components/CallDetailOverlay";
import { formatDuration } from "@/lib/format";

const Dashboard = () => {
  const { selectedId, selectedNumber } = useNumbers();
  const { data: stats } = useQuery({
    queryKey: ["stats", selectedId],
    queryFn: () => fetchNumberStats(selectedId),
    enabled: !!selectedId,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });
  const { data: calls = [], isLoading } = useQuery({
    queryKey: ["calls", selectedId],
    queryFn: () => fetchNumberCalls(selectedId),
    enabled: !!selectedId,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  const [selected, setSelected] = useState(null);
  const recent = useMemo(() => calls.slice(0, 5), [calls]);

  return (
    <div data-testid="dashboard-page" className="space-y-6 sm:space-y-8">
      <header>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="font-heading font-bold text-slate-900 text-2xl sm:text-3xl tracking-tight">
            Tableau de bord
          </h1>
          <span className="rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-1 border border-emerald-200" data-testid="selected-line-badge">
            {selectedNumber ? selectedNumber.line_name : "Toutes les lignes"}
          </span>
        </div>
        <p className="text-sm text-slate-500 mt-1">
          {selectedNumber ? `Activité du numéro ${selectedNumber.number}.` : "Activité des deux lignes."}
        </p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
        <StatCard testid="stat-total-calls" label="Appels totaux" value={stats?.total_calls ?? "—"} sub="Sur la période" icon={PhoneCall} accent="emerald" />
        <StatCard testid="stat-answer-rate" label="Taux de réponse" value={stats ? `${stats.answer_rate}%` : "—"} sub={stats ? `${stats.answered_calls} répondus` : ""} icon={TrendingUp} accent="blue" />
        <StatCard testid="stat-missed-calls" label="Appels manqués" value={stats?.missed_calls ?? "—"} sub="À rappeler" icon={PhoneMissed} accent="red" />
        <StatCard testid="stat-avg-duration" label="Durée moyenne" value={stats ? formatDuration(stats.avg_duration_seconds) : "—"} sub="Par appel répondu" icon={Timer} accent="indigo" />
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-slate-900 text-lg">Appels récents</h2>
            <Link to="/appels" data-testid="see-all-calls" className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors">
              Voir tout <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          {isLoading ? (
            <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">Chargement des appels…</div>
          ) : recent.length ? (
            <>
              <div className="hidden md:block">
                <CallList calls={recent} activeId={selected?.id} onSelect={setSelected} />
              </div>
              <div className="md:hidden">
                <CallCards calls={recent} onSelect={setSelected} />
              </div>
            </>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">Aucun appel pour ce numéro.</div>
          )}
        </div>
        <div className="hidden lg:block lg:col-span-4">
          <div className="lg:sticky lg:top-8">
            <CallDetailPanel call={selected} />
          </div>
        </div>
      </div>

      <CallDetailOverlay call={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default Dashboard;
